import { useState, useEffect, useCallback, useMemo } from "react";
import {
  useParams,
  Link,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import communityAPI from "../../../api/community";
import { calculateReadingTime } from "../../../utils/readingTime";
import Avatar from "../../../componentes/ui/Avatar";
import verifiedBadge from "../../../assets/verified.png";
import Badge from "../../../componentes/ui/Badge";
import { useCommunityAuth } from "../../../contexts/CommunityAuthContext";
import { message, Modal } from "antd";
import { PostActions } from "../shared/CRUDActions";
import { PostEditModal } from "../shared/EditModal";
import ReportButton from "../ReportButton";
import ReportModal from "../ReportModal";
import ShareModal from "../shared/ShareModal";
import { FullscreenSpinner, InlineSpinner } from '../../../componentes/LoadingIndicator';
import ReviewFeedbackNotice from "../ReviewFeedbackNotice";
import LoginPrompt from "../shared/LoginPrompt";
import AuthorSpotlight from "../shared/AuthorSpotlight";
import MoreFromAuthor from "../shared/MoreFromAuthor";
import { applyFeedbackHighlights } from "../../../utils/feedbackHighlight";
import { formatDate } from "../../../utils/dateFormat";
import { formatNumber } from "../../../utils/numberFormat";
import { usePost, useComments, usePosts } from "../../../hooks/useCommunity";
import FeaturedArticleFallbackImage from "../../../assets/featuredfallback.webp";
import { truncateText } from "../../../utils/truncateText";
import { Heart, MessageCircle, Eye } from "lucide-react";
const parseApiList = (payload) => (
  Array.isArray(payload) ? payload : payload?.results || []
);

const stripHtmlForExcerpt = (html) => {
  if (!html) return "";
  // DOMParser-based stripping matches BlogList behavior and correctly decodes entities (e.g. &nbsp;).
  if (typeof DOMParser === "undefined") {
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const extractTextExcerpt = (htmlContent, maxLength = 150) => {
  const text = stripHtmlForExcerpt(htmlContent).replace(/\s+/g, " ").trim();
  if (!text) return "";
  return text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}...` : text;
};

const resolveFollowerCount = (source, fallback = 0) => {
  if (!source) {
    return Math.max(0, fallback);
  }
  const numericKeys = ["followers_count", "followers_total", "followers"];
  for (const key of numericKeys) {
    if (typeof source[key] === "number") {
      return Math.max(0, source[key]);
    }
  }
  if (Array.isArray(source.followers)) {
    return source.followers.length;
  }
  return Math.max(0, fallback);
};

const resolveFollowingCount = (source, fallback = 0) => {
  if (!source) {
    return Math.max(0, fallback);
  }
  const numericKeys = ["following_count", "following_total", "following"];
  for (const key of numericKeys) {
    if (typeof source[key] === "number") {
      return Math.max(0, source[key]);
    }
  }
  if (Array.isArray(source.following)) {
    return source.following.length;
  }
  return Math.max(0, fallback);
};

export default function BlogDetail() {
  const { id: slug } = useParams(); // Routes use :id, API accepts slug or numeric id.
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user } = useCommunityAuth();

  // Navigation context
  const fromLearning = searchParams.get("from") === "learning";
  const pathId = searchParams.get("pathId");
  const fromAdmin = Boolean(location.state?.fromAdmin ?? searchParams.get("fromAdmin") === "1");
  const adminBackTo = location.state?.backTo || searchParams.get("backTo") || "/admin/content/blogs";
  const fromReports = Boolean(location.state?.fromReports);
  const fromPublicState = location.state?.fromPublic;
  const isCommunityRoute = location.pathname.startsWith("/community/blog");

  const {
    post: article,
    loading: articleLoading,
    error: articleError,
    refetch: refetchArticle,
  } = usePost(slug);

  const [liked, setLiked] = useState(false);
  const [localLikes, setLocalLikes] = useState(null);
  const [bookmarked, setBookmarked] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState(null); // ID of comment being replied to
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Comment state
  const [expandedComments, setExpandedComments] = useState({});
  const [visibleReplyCounts, setVisibleReplyCounts] = useState({});
  const [commentsToShow, setCommentsToShow] = useState(5);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportingComment, setReportingComment] = useState(null);
  const [submittingReport, setSubmittingReport] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(null);
  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);

  const [editingPost, setEditingPost] = useState(null);
  const [authorFollowMeta, setAuthorFollowMeta] = useState({
    isFollowing: false,
    followers: 0,
  });
  const [authorFollowLoading, setAuthorFollowLoading] = useState(false);
  const [authorProfile, setAuthorProfile] = useState(null);

  const syncAuthorFollowMeta = useCallback((source) => {
    if (!source) {
      return;
    }
    setAuthorFollowMeta((prev) => {
      const nextFollowers = resolveFollowerCount(source, prev.followers);
      const nextIsFollowing =
        typeof source.is_following === "boolean"
          ? source.is_following
          : prev.isFollowing;

      if (
        nextFollowers === prev.followers &&
        nextIsFollowing === prev.isFollowing
      ) {
        return prev;
      }

      return {
        isFollowing: nextIsFollowing,
        followers: nextFollowers,
      };
    });
  }, []);

  const highlightedContent = useMemo(() => {
    if (!article?.content) {
      return "";
    }
    const highlights = article.review_feedback?.highlights;
    if (
      article.is_approved ||
      !Array.isArray(highlights) ||
      highlights.length === 0
    ) {
      return article.content;
    }
    return applyFeedbackHighlights(article.content, highlights);
  }, [article]);

  const displayContent = useMemo(() => {
    let html = highlightedContent || "";
    // Replace non-breaking spaces with regular space so the browser can wrap lines
    // Also remove zero-width spaces/soft hyphens to prevent weird intra-word breaks
    html = html.replace(/(&nbsp;|\u00A0)/g, " ");
    html = html.replace(/[\u00AD\u200B\u200C\u200D\u200E\u200F\uFEFF]/g, "");
    return html;
  }, [highlightedContent]);

  const heroImage = useMemo(() => {
    if (!article) {
      return null;
    }
    return article.cover_image_url || article.image || null;
  }, [article]);

  const reviewStatus = article?.review_status || 'pending_review';
  const statusMeta = useMemo(() => {
    if (!article || article.is_approved) {
      return null;
    }
    if (reviewStatus === 'requires_revision') {
      return { color: 'orange', label: 'Needs Your Revision' };
    }
    if (reviewStatus === 'pending_review') {
      return { color: 'yellow', label: 'Awaiting Moderator Review' };
    }
    return null;
  }, [article, reviewStatus]);

  const {
    fetchComments: refetchComments,
    createComment,
    updateComment,
    deleteComment,
    toggleLike: toggleCommentLike,
  } = useComments(article?.id, { enabled: Boolean(article?.id) });

  const relatedQuery = useQuery({
    queryKey: [
      "community",
      "posts",
      "blog",
      "related",
      { category: article?.category?.id, articleId: article?.id }
    ],
    enabled: Boolean(article?.id && article?.category?.id),
    queryFn: async () => {
      const res = await communityAPI.posts.getAll({
        post_type: "blog",
        category: article.category.id,
        ordering: "-views"
      });
      const list = parseApiList(res.data);
      return list.filter((p) => p.id !== article.id).slice(0, 3);
    },
    staleTime: 60_000
  });

  const featuredQuery = useQuery({
    queryKey: ["community", "posts", "blog", "featured"],
    enabled: Boolean(article?.id),
    queryFn: async () => {
      const res = await communityAPI.posts.getFeatured();
      return parseApiList(res.data);
    },
    staleTime: 60_000
  });

  const authorPostsFilters = useMemo(() => {
    if (!article?.author?.username) {
      return { post_type: "blog", ordering: "-created_at" };
    }
    return {
      post_type: "blog",
      author: article.author.username,
      ordering: "-created_at",
    };
  }, [article?.author?.username]);

  const { posts: authorPostCollection = [], loading: authorPostsLoading } = usePosts(
    authorPostsFilters,
    { paginated: false, pageSize: 12, skip: !article?.author?.username }
  );

  const relatedPosts = relatedQuery.data ?? [];

  const featuredPosts = useMemo(() => {
    const base = featuredQuery.data ?? [];
    if (!article) {
      return base.slice(0, 3);
    }
    const excludeIds = new Set([article.id, ...relatedPosts.map((post) => post.id)]);
    return base.filter((post) => !excludeIds.has(post.id)).slice(0, 3);
  }, [featuredQuery.data, relatedPosts, article]);

  const moreFromAuthorPosts = useMemo(() => {
    if (!article?.author?.username) {
      return [];
    }
    return (authorPostCollection || [])
      .filter((entry) => entry.id !== article.id)
      .slice(0, 3);
  }, [authorPostCollection, article?.author?.username, article?.id]);


  const refreshDiscussion = useCallback(async () => {
    await Promise.all([refetchArticle(), refetchComments()]);
  }, [refetchArticle, refetchComments]);

  const resolveCardImage = useCallback((post) => {
    if (!post) {
      return null;
    }
    return post.cover_image_url || post.image || FeaturedArticleFallbackImage;
  }, []);

  useEffect(() => {
    if (articleError) {
      message.error("Article not found");
    }
  }, [articleError]);

  useEffect(() => {
    if (!article) {
      setLiked(false);
      setLocalLikes(null);
      setBookmarked(false);
      return;
    }
    const likes = article.likes_total ?? article.likes_count ?? 0;
    setLiked(Boolean(article.user_has_liked));
    setLocalLikes(likes);
    setBookmarked(Boolean(article.user_has_bookmarked));
  }, [article]);



  useEffect(() => {
    setVisibleReplyCounts({});
    setCommentsToShow(5);
  }, [article?.id]);

  useEffect(() => {
    setAuthorProfile(article?.author || null);
  }, [article?.author]);

  useEffect(() => {
    syncAuthorFollowMeta(article?.author);
  }, [
    article?.author,
    article?.author?.username,
    article?.author?.followers_count,
    article?.author?.is_following,
    syncAuthorFollowMeta,
  ]);

  useEffect(() => {
    if (!article?.author?.username) {
      return;
    }
    let cancelled = false;
    const loadFollowSnapshot = async () => {
      try {
        const { data } = await communityAPI.users.getByUsername(
          article.author.username
        );
        if (!cancelled) {
          syncAuthorFollowMeta(data);
          setAuthorProfile(data);
        }
      } catch (err) {
        console.error('Failed to refresh author follow stats', err);
      }
    };
    loadFollowSnapshot();
    return () => {
      cancelled = true;
    };
  }, [article?.author?.username, syncAuthorFollowMeta]);

  const resolvedAuthor = authorProfile || article?.author || null;
  const authorUsername = resolvedAuthor?.username;
  const rawFirstName = resolvedAuthor?.first_name || resolvedAuthor?.profile?.first_name || "";
  const rawLastName = resolvedAuthor?.last_name || resolvedAuthor?.profile?.last_name || "";
  const calculatedFullName = [rawFirstName, rawLastName].filter(Boolean).join(" ").trim();
  const authorDisplayName =
    calculatedFullName || resolvedAuthor?.name || authorUsername || "Community Member";
  const authorHeadline = resolvedAuthor?.title || resolvedAuthor?.role || "";
  const rawAuthorBio = resolvedAuthor?.bio ?? resolvedAuthor?.profile?.bio;
  const authorBio = useMemo(() => {
    return typeof rawAuthorBio === "string" ? rawAuthorBio.trim() : "";
  }, [rawAuthorBio]);
  const authorFollowingCount = useMemo(
    () => resolveFollowingCount(resolvedAuthor),
    [
      resolvedAuthor?.following_count,
      resolvedAuthor?.following_total,
      resolvedAuthor?.following,
    ]
  );
  const authorProfileLink = authorUsername ? `/profile/${authorUsername}` : null;
  const authorJoinedDate =
    resolvedAuthor?.date_joined || resolvedAuthor?.profile?.date_joined || null;
  const authorAvatar =
    resolvedAuthor?.avatar_url ||
    resolvedAuthor?.avatar ||
    resolvedAuthor?.profile?.avatar_url ||
    resolvedAuthor?.profile?.avatar ||
    null;
  const rawAuthorSkills = resolvedAuthor?.skills || resolvedAuthor?.profile?.skills;
  const authorSkills = useMemo(() => {
    if (!Array.isArray(rawAuthorSkills)) {
      return [];
    }
    return rawAuthorSkills
      .map((skill) => (typeof skill === "string" ? skill.trim() : ""))
      .filter(Boolean)
      .slice(0, 6);
  }, [rawAuthorSkills]);
  const authorId = resolvedAuthor?.id ?? article?.author?.id ?? null;
  const isAuthor = Boolean(user && authorId && user.id === authorId);
  const showAuthorFollowCta = Boolean(authorUsername) && !isAuthor;
  const authorFollowerLabel =
    authorFollowMeta.followers === 1 ? "Follower" : "Followers";

  const displayedLikes = localLikes ?? (article?.likes_total ?? article?.likes_count ?? 0);

  const handleLike = async () => {
    if (!user) {
      setShowLoginPrompt("like");
      return;
    }
    if (article && !article.is_approved) {
      message.warning("This post is pending approval. Interactions are disabled.");
      return;
    }
    if (!slug) {
      return;
    }

    const previousLiked = liked;
    const previousLikes = displayedLikes;
    const nextLiked = !previousLiked;

    setLiked(nextLiked);
    setLocalLikes((prev) => {
      const base = prev ?? (article?.likes_total ?? article?.likes_count ?? 0);
      const delta = nextLiked ? 1 : -1;
      const next = base + delta;
      return next < 0 ? 0 : next;
    });

    try {
      await communityAPI.posts.toggleLike(slug);
      refreshDiscussion().catch(() => { });
    } catch (err) {
      console.error("Error toggling like:", err);
      message.error("Unable to update reaction");
      setLiked(previousLiked);
      setLocalLikes(previousLikes);
    }
  };

  const handleBookmark = async () => {
    if (!user) {
      setShowLoginPrompt("bookmark");
      return;
    }
    if (article && !article.is_approved) {
      message.warning("This post is pending approval. Interactions are disabled.");
      return;
    }
    if (!article?.id) {
      return;
    }
    try {
      const res = await communityAPI.progress.toggleBookmark(article.id);
      const next = Boolean(res?.data?.bookmarked ?? res?.bookmarked);
      setBookmarked(next);
      message.success(next ? "Bookmarked!" : "Bookmark removed");
      refreshDiscussion().catch(() => { });
    } catch (err) {
      console.error("Error toggling bookmark:", err);
      message.error("Error toggling bookmark");
    }
  };

  const handleAuthorFollowToggle = async () => {
    if (!authorUsername) {
      return;
    }
    if (!user) {
      setShowLoginPrompt("follow");
      return;
    }
    if (isAuthor) {
      message.info("You cannot follow yourself.");
      return;
    }
    if (authorFollowLoading) {
      return;
    }

    const previousState = authorFollowMeta;
    const optimisticFollowing = !previousState.isFollowing;

    setAuthorFollowMeta({
      isFollowing: optimisticFollowing,
      followers: Math.max(
        0,
        previousState.followers + (optimisticFollowing ? 1 : -1)
      ),
    });
    setAuthorFollowLoading(true);

    const action = previousState.isFollowing
      ? communityAPI.users.unfollow
      : communityAPI.users.follow;

    try {
      const { data } = await action(authorUsername);
      syncAuthorFollowMeta({
        followers_count:
          typeof data?.followers_count === "number"
            ? data.followers_count
            : typeof data?.followers === "number"
              ? data.followers
              : undefined,
        is_following:
          typeof data?.is_following === "boolean"
            ? data.is_following
            : undefined,
      });
      message.success(
        previousState.isFollowing
          ? "You are no longer following this author."
          : "You are now following this author."
      );
    } catch (err) {
      console.error("Failed to toggle follow", err);
      setAuthorFollowMeta({
        isFollowing: previousState.isFollowing,
        followers: previousState.followers,
      });
      message.error(
        previousState.isFollowing
          ? "Unable to unfollow right now."
          : "Unable to follow this author right now."
      );
    } finally {
      setAuthorFollowLoading(false);
    }
  };

  const handleComment = async () => {
    if (commentLoading) return;
    if (!user) {
      setShowLoginPrompt(replyingTo ? "reply" : "comment");
      return;
    }
    if (article && !article.is_approved) {
      message.warning("This post is pending approval. Comments are disabled.");
      return;
    }
    if (!newComment.trim()) return;
    try {
      setCommentLoading(true);
      await createComment({
        content: newComment,
        parent: replyingTo || null,
      });
      setNewComment("");
      setReplyingTo(null);
      refreshDiscussion().catch(() => { });
    } catch (err) {
      console.error("Error adding comment:", err);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleCommentLike = async (commentId) => {
    if (!user) {
      setShowLoginPrompt("like");
      return;
    }
    if (article && !article.is_approved) {
      message.warning("This post is pending approval. Interactions are disabled.");
      return;
    }
    try {
      await toggleCommentLike(commentId);
      refreshDiscussion().catch(() => { });
    } catch (err) {
      console.error("Error liking comment:", err);
    }
  };

  const beginCommentEdit = (comment) => {
    if (!user) {
      setShowLoginPrompt("edit");
      return;
    }
    if (article && !article.is_approved) {
      message.warning("This post is pending approval. Editing is disabled.");
      return;
    }
    setEditingCommentId(comment.id);
    setEditContent(comment.content || "");
    setReplyingTo(null);
    setNewComment("");
  };

  const cancelCommentEdit = () => {
    setEditingCommentId(null);
    setEditContent("");
  };

  const handleCommentUpdate = async (commentId) => {
    if (article && !article.is_approved) {
      message.warning("This post is pending approval. Editing is disabled.");
      return;
    }
    if (!editContent.trim()) {
      message.error("Comment cannot be empty");
      return;
    }
    try {
      await updateComment(commentId, { content: editContent });
      cancelCommentEdit();
      refreshDiscussion().catch(() => { });
    } catch (err) {
      console.error("Error updating comment:", err);
    }
  };

  const handleCommentDelete = async (commentId) => {
    if (article && !article.is_approved) {
      message.warning("This post is pending approval. Deleting is disabled.");
      return;
    }
    try {
      await deleteComment(commentId);
      if (editingCommentId === commentId) {
        cancelCommentEdit();
      }
      if (replyingTo === commentId) {
        setReplyingTo(null);
      }
      refreshDiscussion().catch(() => { });
    } catch (err) {
      console.error("Error deleting comment:", err);
    }
  };

  const confirmCommentDelete = (commentId) => {
    if (article && !article.is_approved) {
      message.warning("This post is pending approval. Deleting is disabled.");
      return;
    }
    Modal.confirm({
      title: "Delete comment?",
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => handleCommentDelete(commentId),
    });
  };

  const openReportModal = (comment) => {
    if (!user) {
      setShowLoginPrompt("report");
      return;
    }
    if (article && !article.is_approved) {
      message.warning("This post is pending approval. Reporting is disabled.");
      return;
    }
    if (comment.author?.id === user?.id) {
      message.warning("You cannot report your own comment");
      return;
    }
    setReportingComment(comment);
    setReportModalOpen(true);
  };

  const closeReportModal = () => {
    setReportModalOpen(false);
    setReportingComment(null);
  };

  const submitReport = async ({ reason, description }) => {
    if (!reportingComment) {
      message.error("No comment selected");
      return;
    }
    setSubmittingReport(true);
    try {
      await communityAPI.comments.report(reportingComment.id, { reason, description });
      message.success("Report submitted for review.");
      closeReportModal();
    } catch (err) {
      console.error("Failed to submit report", err);
      const data = err.response?.data;
      let detail = data?.detail;
      if (!detail && data) {
        const firstKey = Object.keys(data)[0];
        const value = data[firstKey];
        detail = Array.isArray(value) ? value[0] : value;
      }
      message.error(detail || "Unable to submit report");
    } finally {
      setSubmittingReport(false);
    }
  };

  // Recursive comment rendering
  const renderComment = (comment, depth = 0) => {
    const indentClasses = ["ml-0", "ml-8", "ml-16", "ml-24"];
    const indentClass = indentClasses[Math.min(depth, indentClasses.length - 1)];
    const isEditing = editingCommentId === comment.id;
    const rawContent = comment.content || "";
    const previewLimit = 300;
    const needsClamp = !isEditing && rawContent.length > previewLimit;
    const isExpanded = !!expandedComments[comment.id];
    const displayedContent = !needsClamp || isExpanded
      ? rawContent
      : `${rawContent.substring(0, previewLimit)}...`;

    const childReplies = comment.replies || [];
    const totalReplies = childReplies.length;
    const baseVisibleReplies = depth === 0 ? 3 : 2;
    const loadIncrement = depth === 0 ? 5 : 3;
    const currentVisibleCount = visibleReplyCounts[comment.id] ?? baseVisibleReplies;
    const effectiveVisibleCount = Math.min(totalReplies, currentVisibleCount);
    const visibleReplies = childReplies.slice(0, effectiveVisibleCount);
    const hiddenCount = totalReplies - visibleReplies.length;
    const nextBatchCount = hiddenCount > 0 ? Math.min(loadIncrement, hiddenCount) : 0;
    const replyIndentClass = indentClasses[Math.min(depth + 1, indentClasses.length - 1)];
    const canCollapseReplies = totalReplies > baseVisibleReplies && effectiveVisibleCount > baseVisibleReplies;
    const canManage = Boolean(user && comment.author?.id === user.id);
    const canReport = Boolean(user && comment.author?.id !== user.id);

    const commentFirstName = comment.author?.first_name || comment.author?.profile?.first_name || "";
    const commentLastName = comment.author?.last_name || comment.author?.profile?.last_name || "";
    const commentFullName = [commentFirstName, commentLastName].filter(Boolean).join(" ").trim();
    const commentDisplayName = commentFullName || comment.author?.name || comment.author?.display_name || comment.author?.username || "Anonymous";

    return (
      <div key={comment.id} className={`${depth > 0 ? indentClass : ""} mt-5`}>
        <div className={`flex items-start gap-3 ${depth > 0 ? 'pl-4 border-l-2 border-gray-200 dark:border-gray-700' : ''}`}>
          <Avatar name={commentDisplayName} size={depth === 0 ? 10 : 8} src={comment.author?.avatar_url || comment.author?.avatar} />
          <div className="flex-1 min-w-0">
            <div className="bg-gray-50 dark:bg-gray-800/60 rounded-2xl px-4 py-3">
              <div className="flex items-center justify-between mb-1">
                <div>
                  {comment.author?.username ? (
                    <Link to={`/profile/${comment.author.username}`} className="font-semibold text-gray-900 dark:text-white text-sm hover:text-primary transition-colors flex items-center gap-1.5">
                      {commentDisplayName}
                      {(comment.author.is_trusted || comment.author.status?.is_trusted) && (
                        <img
                          src={verifiedBadge}
                          alt="Verified"
                          className="w-3.5 h-3.5 shrink-0 drop-shadow-sm translate-y-[0.5px]"
                        />
                      )}
                    </Link>
                  ) : (
                    <span className="font-semibold text-gray-900 dark:text-white text-sm">{commentDisplayName}</span>
                  )}
                  <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">{formatDate(comment.created_at)}</span>
                </div>
              </div>
              {isEditing ? (
                <div className="space-y-3 mt-2">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                    rows={3}
                    placeholder="Update your comment"
                  />
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCommentUpdate(comment.id)}
                        className="px-3 py-1.5 text-xs rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-medium transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelCommentEdit}
                        className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                    {canManage && (
                      <button
                        onClick={() => confirmCommentDelete(comment.id)}
                        className="px-3 py-1.5 text-xs rounded-lg border border-red-200 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed break-words">
                    {displayedContent}
                  </p>
                  {needsClamp && (
                    <button
                      onClick={() => setExpandedComments(prev => ({ ...prev, [comment.id]: !prev[comment.id] }))}
                      className="text-teal-600 dark:text-teal-400 text-xs font-medium hover:underline mt-1"
                    >
                      {isExpanded ? "Show less" : "Read more"}
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-3 mt-2 px-1">
              <button
                onClick={() => handleCommentLike(comment.id)}
                disabled={!article?.is_approved}
                className={`flex items-center gap-1.5 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${comment.user_has_liked ? "text-red-500" : "text-gray-400 dark:text-gray-500 hover:text-red-500"}`}
              >
                <svg className={`w-3.5 h-3.5`} fill={comment.user_has_liked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                {formatNumber(comment.likes_count || 0)}
              </button>
              <button
                onClick={() => {
                  if (!user) { setShowLoginPrompt("reply"); return; }
                  if (!article?.is_approved) { message.warning("This post is pending approval. Comments are disabled."); return; }
                  setEditingCommentId(null); setEditContent("");
                  setReplyingTo(replyingTo === comment.id ? null : comment.id);
                  setNewComment("");
                }}
                disabled={!user || !article?.is_approved}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                Reply
              </button>
              {canReport && (
                <ReportButton
                  onClick={() => openReportModal(comment)}
                  disabled={!user || comment.author?.id === user?.id || !article?.is_approved}
                />
              )}
              {canManage && (
                <>
                  <button onClick={() => beginCommentEdit(comment)} disabled={!article?.is_approved} className="text-xs text-gray-400 dark:text-gray-500 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Edit</button>
                  <button onClick={() => confirmCommentDelete(comment.id)} disabled={!article?.is_approved} className="text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Delete</button>
                </>
              )}
            </div>
          </div>
        </div>

        {replyingTo === comment.id && (
          <div className={`${indentClasses[Math.min(depth + 1, indentClasses.length - 1)]} mt-3`}>
            <div className="bg-gray-50 dark:bg-gray-800/60 rounded-2xl p-4 border border-gray-200/60 dark:border-gray-700/60">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                rows={3}
                placeholder={`Reply to ${comment.author?.username}...`}
              />
              <div className="flex justify-end gap-2 mt-3">
                <button
                  onClick={() => { setReplyingTo(null); setNewComment(""); }}
                  disabled={commentLoading}
                  className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
                >Cancel</button>
                <button
                  onClick={handleComment}
                  disabled={commentLoading}
                  className="px-4 py-1.5 text-xs rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-medium transition-colors disabled:opacity-75"
                >
                  {commentLoading ? (
                    <InlineSpinner tone="slate" message="Posting reply..." messageClassName="text-white" />
                  ) : "Reply"}
                </button>
              </div>
            </div>
          </div>
        )}

        {visibleReplies.map((child) => renderComment(child, depth + 1))}

        {hiddenCount > 0 && (
          <button
            onClick={() => {
              setVisibleReplyCounts((prev) => {
                const current = prev[comment.id] ?? baseVisibleReplies;
                const next = Math.min(totalReplies, current + loadIncrement);
                return { ...prev, [comment.id]: next };
              });
            }}
            className={`${replyIndentClass} mt-2 text-primary text-xs font-medium hover:underline`}
          >
            Show {formatNumber(nextBatchCount)} more {nextBatchCount === 1 ? "reply" : "replies"}
          </button>
        )}

        {canCollapseReplies && (
          <button
            onClick={() => {
              setVisibleReplyCounts((prev) => {
                if (!(comment.id in prev)) return prev;
                const next = { ...prev };
                delete next[comment.id];
                return next;
              });
            }}
            className={`${replyIndentClass} mt-1 text-gray-400 dark:text-gray-500 text-xs font-medium hover:underline`}
          >
            Show fewer replies
          </button>
        )}
      </div>
    );
  };

  if (articleLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0E0E0E] pt-24 flex items-center justify-center blog-body">
        <FullscreenSpinner message="Loading article..." tone="slate" />
      </div>
    );
  }
  if (!article) return (
    <div className="min-h-screen flex items-center justify-center pt-24 bg-white dark:bg-[#0E0E0E] blog-body">
      <div className="text-center">
        <p className="text-5xl mb-4">📄</p>
        <p className="text-xl font-semibold text-gray-700 dark:text-gray-300">Article not found</p>
        <p className="text-gray-500 mt-2">The article you're looking for doesn't exist or was removed.</p>
      </div>
    </div>
  );

  const isPublicContext = fromPublicState !== undefined ? Boolean(fromPublicState) : !isCommunityRoute;
  const defaultBackTarget = isAuthor && !isPublicContext ? "/community/blog" : "/blog";
  const backTarget = fromReports
    ? "/admin/reports"
    : fromAdmin
      ? adminBackTo
      : fromLearning && pathId
        ? `/community/learning/${pathId}`
        : defaultBackTarget;
  const backLabel = fromReports
    ? "Back to Reports"
    : fromAdmin
      ? "Back to admin"
      : fromLearning && pathId
        ? "Back to Learning Path"
        : isAuthor && !isPublicContext
          ? "Back to My Blog"
          : "Back to Blog";

  const buildPostLink = (post) => {
    const base = isPublicContext ? "/blog" : "/community/blog";
    if (!post) {
      return base;
    }
    return post.slug ? `${base}/${post.slug}` : `${base}/${post.id}`;
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0E0E0E] pt-16 sm:pt-24 blog-body">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap');
        .blog-title { font-family: 'Crimson Pro', Georgia, serif; }
        .blog-body { font-family: 'DM Sans', sans-serif; }
        .prose-blog { 
          font-family: 'DM Sans', sans-serif; 
          font-size: 1.0625rem; 
          line-height: 1.8; 
          color: #2d2d2d;
          word-break: normal;
          overflow-wrap: break-word !important; /* Allow long words/URLs to wrap safely */
          word-wrap: break-word !important;
          hyphens: none !important;
          -webkit-hyphens: none !important;
        }
        .prose-blog * {
          white-space: normal !important; /* Force standard text flow if editor applied nowrap */
          hyphens: none !important;
          -webkit-hyphens: none !important;
        }
        .dark .prose-blog { color: #d1d5db; }
        .prose-blog h1,.prose-blog h2,.prose-blog h3,.prose-blog h4 {
          font-family: 'Crimson Pro', Georgia, serif;
          font-weight: 600;
          margin-top: 1.6em;
          margin-bottom: 0.6em;
          line-height: 1.3;
          letter-spacing: -0.01em;
        }
        .prose-blog p { margin-bottom: 1.35em; }
        .prose-blog strong, .prose-blog b { font-weight: 700 !important; }

        /* Links */
        .prose-blog a {
          color: #2Ba8CB;
          text-decoration: underline;
          text-underline-offset: 3px;
          text-decoration-color: rgba(43, 168, 203, 0.35);
          transition: color 0.2s ease, text-decoration-color 0.2s ease;
        }
        .prose-blog a:hover { color: #1e8fa8; text-decoration-color: #1e8fa8; }

        /* Lists */
        .prose-blog ul, .prose-blog ol { padding-left: 0.25rem; margin-top: 0.75em; margin-bottom: 0.75em; }
        .prose-blog ul { list-style: none; }
        .prose-blog ul > li, .prose-blog ol > li {
          position: relative;
          padding-left: 1.75em;
          margin-bottom: 0.5em;
          line-height: 1.7;
        }
        .prose-blog ul > li::before {
          content: "";
          position: absolute;
          left: 0.25em;
          top: 0.6em;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2Ba8CB 0%, #1e8fa8 100%);
          box-shadow: 0 0 6px rgba(43, 168, 203, 0.35);
        }
        .prose-blog ul ul > li::before {
          background: transparent;
          border: 2px solid #2Ba8CB;
          box-shadow: none;
          width: 7px;
          height: 7px;
        }
        .prose-blog ul ul ul > li::before {
          border: none;
          border-radius: 0;
          background: #2Ba8CB;
          width: 10px;
          height: 2px;
          top: 0.75em;
        }
        .prose-blog ol { list-style: none; counter-reset: blog-ol; }
        .prose-blog ol > li { counter-increment: blog-ol; }
        .prose-blog ol > li::before {
          content: counter(blog-ol) ".";
          position: absolute;
          left: 0;
          top: 0;
          font-weight: 700;
          font-size: 0.9em;
          color: #2Ba8CB;
          font-variant-numeric: tabular-nums;
          min-width: 1.5em;
        }
        .prose-blog ul ul, .prose-blog ol ol, .prose-blog ul ol, .prose-blog ol ul {
          margin-top: 0.35em;
          margin-bottom: 0.35em;
        }
        /* Dark mode bullets */
        .dark .prose-blog ul > li::before {
          background: linear-gradient(135deg, #38bdf8 0%, #2Ba8CB 100%);
          box-shadow: 0 0 6px rgba(56, 189, 248, 0.3);
        }
        .dark .prose-blog ul ul > li::before { background: transparent; border-color: #38bdf8; box-shadow: none; }
        .dark .prose-blog ul ul ul > li::before { border: none; background: #38bdf8; }
        .dark .prose-blog ol > li::before { color: #38bdf8; }

        /* Blockquotes */
        .prose-blog blockquote {
          border-left: 3px solid #2Ba8CB;
          padding: 0.75rem 1.25rem;
          margin: 1.5em 0;
          background: rgba(43, 168, 203, 0.04);
          border-radius: 0 8px 8px 0;
          color: #6b7280;
        }
        .dark .prose-blog blockquote { background: rgba(43, 168, 203, 0.08); color: #a1a1aa; }

        /* Code */
        .prose-blog code { background: #f0faf9; color: #2Ba8CB; padding: 0.15em 0.45em; border-radius: 5px; font-size: 0.88em; font-weight: 500; }
        .dark .prose-blog code { background: rgba(43, 168, 203, 0.15); color: #5cc9e0; }
        .prose-blog pre {
          background: #1a1a2e; color: #e2e8f0; padding: 1.25rem 1.5rem; border-radius: 12px;
          overflow-x: auto; margin: 1.5em 0; font-size: 0.88em; line-height: 1.7;
          border: 1px solid rgba(255, 255, 255, 0.06);
        }
        .prose-blog pre code { background: transparent; color: inherit; padding: 0; border-radius: 0; font-size: inherit; font-weight: 400; }

        /* Tables */
        .prose-blog table { width: 100%; border-collapse: collapse; margin: 1.5em 0; font-size: 0.925em; }
        .prose-blog thead th {
          background: #f8fafc; font-weight: 600; text-align: left; padding: 0.65em 1em;
          border-bottom: 2px solid #e2e8f0; color: #374151; font-size: 0.85em;
          text-transform: uppercase; letter-spacing: 0.04em;
        }
        .dark .prose-blog thead th { background: rgba(255, 255, 255, 0.04); border-bottom-color: #374151; color: #d1d5db; }
        .prose-blog tbody td { padding: 0.6em 1em; border-bottom: 1px solid #f1f5f9; }
        .dark .prose-blog tbody td { border-bottom-color: #1f2937; }
        .prose-blog tbody tr:hover { background: rgba(43, 168, 203, 0.03); }
        .dark .prose-blog tbody tr:hover { background: rgba(43, 168, 203, 0.06); }

        /* Images & HR */
        .prose-blog img { border-radius: 12px; margin: 1.5em 0; }
        .prose-blog hr {
          border: none; height: 1px; margin: 2em 0;
          background: linear-gradient(90deg, transparent, #d1d5db 20%, #d1d5db 80%, transparent);
        }
        .dark .prose-blog hr { background: linear-gradient(90deg, transparent, #374151 20%, #374151 80%, transparent); }

        /* Mark / Highlight */
        .prose-blog mark { background: linear-gradient(120deg, #fff3b0 0%, #ffe066 100%); color: inherit; padding: 0.1em 0.2em; border-radius: 3px; }
        .dark .prose-blog mark { background: linear-gradient(120deg, rgba(255, 243, 176, 0.25) 0%, rgba(255, 224, 102, 0.25) 100%); }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10 items-start">
          <div className="min-w-0">
            {/* Back Navigation */}
            <Link
              to={backTarget}
              className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-primary font-medium mt-3 mb-3 transition-colors duration-200 group"
            >
              <svg className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              {backLabel}
            </Link>

            <article className="bg-white dark:bg-[#161616] rounded-3xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
              {heroImage && (
                <div className="relative h-72 sm:h-96 overflow-hidden">
                  <img src={heroImage} alt={article.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  {article.category && (
                    <div className="absolute top-5 left-6">
                      <span className="px-3 py-1 text-xs font-semibold tracking-widest uppercase bg-primary text-white rounded-full shadow-lg">
                        {article.category.name}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="p-7 sm:p-12">
                <header className="mb-8">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <h1 className="blog-title text-3xl sm:text-5xl font-bold text-gray-950 dark:text-white leading-tight tracking-tight">{article.title}</h1>
                    </div>
                    {isAuthor && (
                      <PostActions
                        post={article}
                        onEdit={() => setEditingPost(article)}
                        onDeleted={() => navigate(backTarget)}
                      />
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-4 mt-5 pb-6 border-b border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-3 flex-1 min-w-[220px]">
                      <Avatar
                        name={authorDisplayName}
                        size={10}
                        src={authorAvatar}
                      />
                      <div>
                        {authorUsername ? (
                          <Link
                            to={`/profile/${authorUsername}`}
                            className="font-semibold text-gray-900 dark:text-white hover:text-primary transition-colors text-sm flex items-center gap-1.5"
                          >
                            {truncateText(authorDisplayName, 25)}
                            {(article.author.is_trusted || article.author.status?.is_trusted) && (
                              <img
                                src={verifiedBadge}
                                alt="Verified"
                                className="w-4 h-4 shrink-0 drop-shadow-sm translate-y-[0.5px]"
                              />
                            )}
                          </Link>
                        ) : (
                          <div className="font-semibold text-gray-900 dark:text-white text-sm">Anonymous</div>
                        )}
                        <div className="flex flex-wrap items-center gap-2 text-gray-400 dark:text-gray-500 text-xs mt-0.5">
                          <span>{formatDate(article.created_at)} · {calculateReadingTime(article.content)} min read</span>
                          {statusMeta && (
                            <Badge color={statusMeta.color} className="text-xs whitespace-nowrap">{statusMeta.label}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {article.tags && article.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {article.tags.map(tag => (
                        <span key={tag.id || tag} className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:text-teal-600 dark:hover:text-teal-400 transition-colors cursor-default">
                          #{typeof tag === 'string' ? tag : tag.name}
                        </span>
                      ))}
                    </div>
                  )}
                </header>

                {statusMeta && article.review_feedback?.message && (
                  <div className="mb-8">
                    <ReviewFeedbackNotice feedback={article.review_feedback} />
                  </div>
                )}

                {/* Top Interaction Bar */}
                <div className="flex items-center justify-between gap-2 mb-8 py-4 border-b border-gray-100 dark:border-gray-800 overflow-x-auto scrollbar-hide">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <button onClick={handleLike} disabled={!article.is_approved} className={`action-pill ${liked ? "bg-red-50 dark:bg-red-900/20 text-red-500" : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-red-500"} disabled:opacity-50`}>
                      <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} />
                      {formatNumber(displayedLikes)}
                    </button>
                    <button onClick={() => setCommentDrawerOpen(true)} className="action-pill text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-primary">
                      <MessageCircle className="w-4 h-4" />
                      {formatNumber(article.comments?.length || 0)}
                    </button>
                    <button onClick={handleBookmark} disabled={!article.is_approved} className={`action-pill ${bookmarked ? "bg-amber-50 dark:bg-amber-900/20 text-amber-500" : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-amber-500"} disabled:opacity-50`}>
                      <svg className="w-4 h-4" fill={bookmarked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
                    </button>
                    <button onClick={() => setShareModalOpen(true)} disabled={!article.is_approved} className="action-pill text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-primary disabled:opacity-50">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" /></svg>
                      <span className="hidden sm:inline font-medium">Share</span>
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500 font-medium action-pill border-none bg-transparent cursor-default px-1 sm:px-3">
                    <Eye className="w-4 h-4" />
                    <span className="text-xs">{formatNumber(article.views || 0)}</span>
                  </div>
                </div>


                <div
                  className="prose-blog prose prose-lg dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: displayContent }}
                />

                <div className="flex items-center justify-between gap-2 mt-10 pt-6 border-t border-gray-100 dark:border-gray-800 overflow-x-auto scrollbar-hide">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <button onClick={handleLike} disabled={!article.is_approved} className={`action-pill ${liked ? "bg-red-50 dark:bg-red-900/20 text-red-500" : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-red-50 hover:text-red-500"} disabled:opacity-50`}>
                      <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} />
                      {formatNumber(article.likes_count || 0)}
                    </button>
                    <button onClick={handleBookmark} disabled={!article.is_approved} className={`action-pill ${bookmarked ? "bg-amber-50 dark:bg-amber-900/20 text-amber-500" : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-amber-50 hover:text-amber-500"} disabled:opacity-50`}>
                      <svg className="w-4 h-4" fill={bookmarked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
                      <span className="hidden sm:inline font-medium">Bookmark</span>
                    </button>
                    <button onClick={() => setShareModalOpen(true)} disabled={!article.is_approved} className="action-pill bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-blue-50 hover:text-primary disabled:opacity-50">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" /></svg>
                      <span className="hidden sm:inline font-medium">Share</span>
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500 font-medium action-pill border-none bg-transparent cursor-default px-1 sm:px-3">
                    <Eye className="w-4 h-4" />
                    <span className="text-xs">{formatNumber(article.views || 0)}</span>
                  </div>
                </div>


              </div>
            </article>

            {resolvedAuthor && (
              <section className="mt-12 space-y-8">
                <AuthorSpotlight
                  authorName={authorDisplayName}
                  username={authorUsername}
                  avatar={authorAvatar}
                  isTrusted={resolvedAuthor?.is_trusted || resolvedAuthor?.status?.is_trusted}
                  headline={authorHeadline}
                  bio={authorBio}
                  skills={authorSkills}
                  followerCount={authorFollowMeta.followers}
                  followerLabel={authorFollowerLabel}
                  followingCount={authorFollowingCount}
                  joinedDate={authorJoinedDate}
                  showFollowButton={showAuthorFollowCta}
                  onFollowToggle={handleAuthorFollowToggle}
                  followLoading={authorFollowLoading}
                  isFollowing={authorFollowMeta.isFollowing}
                  profileLink={authorProfileLink}
                  selfNotice="You authored this post"
                />
                <MoreFromAuthor
                  authorName={authorDisplayName}
                  items={moreFromAuthorPosts}
                  loading={authorPostsLoading}
                  profileLink={authorProfileLink}
                  hideWhenEmpty
                  emptyMessage={`No other posts from ${authorDisplayName} yet.`}
                  loadingMessage="Gathering articles..."
                  buildItemLink={buildPostLink}
                  getExcerpt={(item) => extractTextExcerpt(item.content, 200)}
                  getDateLabel={(item) =>
                    item?.created_at ? formatDate(item.created_at) : "Recently"
                  }
                  renderStats={(item) => [
                    <span key="read_time" className="flex items-center gap-1.5">{formatNumber(item.read_time || calculateReadingTime(item.content || ""))} min read</span>,
                    <span key="likes" className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500 hover:text-red-500 transition-colors cursor-pointer"><Heart className="w-3.5 h-3.5" /> {formatNumber(item.likes_total ?? item.likes_count ?? 0)}</span>,
                    <span key="views" className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500"><Eye className="w-3.5 h-3.5" /> {formatNumber(item.views || 0)}</span>,
                  ]}
                />
              </section>
            )}

            {/* Comments Section */}
            <div className="mt-10 bg-white dark:bg-[#161616] rounded-3xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
              <div className="p-7 sm:p-12">
                <h2 className="blog-title text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-7">
                  Comments <span className="text-gray-400 dark:text-gray-600 font-normal text-xl">({formatNumber(article.comments?.length || 0)})</span>
                </h2>
                {!article.is_approved && (
                  <div className="mb-6 text-amber-700 bg-amber-50 dark:text-amber-200 dark:bg-amber-900/20 px-4 py-3 rounded-xl text-sm border border-amber-200 dark:border-amber-800/40">
                    {reviewStatus === 'requires_revision'
                      ? 'This post needs author revision before it can return to the community. Reactions stay disabled until moderators approve it.'
                      : 'This post is awaiting moderator approval. Commenting and reactions are disabled for now.'}
                  </div>
                )}

                {!user && !replyingTo && (
                  <div className="mb-8">
                    <LoginPrompt action="comment" inline />
                  </div>
                )}

                {user && !replyingTo && article.is_approved && (
                  <div className="mb-8 p-5 bg-gray-50 dark:bg-gray-800/50 rounded-2xl">
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Join the discussion</h3>
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                      rows={4}
                      placeholder="Share your thoughts..."
                    />
                    <div className="flex justify-end gap-3 mt-3">
                      <button
                        onClick={() => setNewComment("")}
                        disabled={commentLoading}
                        className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60 transition-colors"
                      >Cancel</button>
                      <button
                        onClick={handleComment}
                        disabled={commentLoading}
                        className="px-6 py-2 rounded-xl bg-primary hover:bg-[#229bb8] text-white text-sm font-semibold disabled:opacity-80 transition-colors shadow-sm"
                      >
                        {commentLoading ? (
                          <InlineSpinner tone="slate" message="Posting comment..." messageClassName="text-white" />
                        ) : "Post comment"}
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {article.comments && article.comments.length > 0 ? (
                    article.comments.slice(0, commentsToShow).map(comment => renderComment(comment))
                  ) : (
                    <div className="text-center py-14">
                      <p className="text-3xl mb-3">💬</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">No comments yet. Be the first to comment!</p>
                    </div>
                  )}

                  {article.comments && article.comments.length > commentsToShow && (
                    <button onClick={() => setCommentsToShow(prev => prev + 5)} className="w-full mt-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      Load more comments ({formatNumber(article.comments.length - commentsToShow)} remaining)
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1 lg:sticky lg:top-24 lg:h-max">
            <div className="space-y-6">

              {/* Related Posts */}
              <div className="bg-white dark:bg-[#161616] rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-800">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Related Posts</h3>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {relatedPosts.length ? (
                    relatedPosts.map((post) => (
                      <Link
                        key={post.id || post.slug || post.title}
                        to={{ pathname: buildPostLink(post), state: { fromPublic: isPublicContext } }}
                        className="block px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
                      >
                        <h4 className="font-medium text-sm text-gray-900 dark:text-white group-hover:text-primary transition-colors line-clamp-2 mb-1">
                          {truncateText(post.title, 60)}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 flex-wrap">
                          <span>{post.category?.name || "General"}</span>
                          <span>·</span>
                          <div className="flex items-center gap-2.5">
                            <span className="flex items-center gap-1" title="Views"><Eye className="w-3.5 h-3.5" /> {formatNumber(post.views || 0)}</span>
                            <span className="flex items-center gap-1" title="Likes"><Heart className="w-3.5 h-3.5" /> {formatNumber(post.likes_count || 0)}</span>
                            <span className="flex items-center gap-1" title="Comments"><MessageCircle className="w-3.5 h-3.5" /> {formatNumber(post.comments_count || 0)}</span>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500 text-center">
                      {article?.category
                        ? "No related posts available yet."
                        : "Explore other categories for more content."}
                    </div>
                  )}
                </div>
              </div>

              {/* Featured Posts */}
              <div className="bg-white dark:bg-[#161616] rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-800">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Featured Posts</h3>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {featuredPosts.length ? (
                    featuredPosts.map((post) => (
                      <Link
                        key={post.id || post.slug || post.title}
                        to={{ pathname: buildPostLink(post), state: { fromPublic: isPublicContext } }}
                        className="block px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
                      >
                        <h4 className="font-medium text-sm text-gray-900 dark:text-white group-hover:text-primary transition-colors line-clamp-2 mb-1">
                          {truncateText(post.title, 60)}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 flex-wrap">
                          <span>{post.category?.name || "General"}</span>
                          <span>·</span>
                          <div className="flex items-center gap-2.5">
                            <span className="flex items-center gap-1" title="Views"><Eye className="w-3.5 h-3.5" /> {formatNumber(post.views || 0)}</span>
                            <span className="flex items-center gap-1" title="Likes"><Heart className="w-3.5 h-3.5" /> {formatNumber(post.likes_count || 0)}</span>
                            <span className="flex items-center gap-1" title="Comments"><MessageCircle className="w-3.5 h-3.5" /> {formatNumber(post.comments_count || 0)}</span>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500 text-center">
                      No featured posts available right now.
                    </div>
                  )}
                </div>
              </div>

            </div>
          </aside>
        </div>
      </div>
      <ReportModal
        isOpen={reportModalOpen}
        onClose={closeReportModal}
        onSubmit={submitReport}
        submitting={submittingReport}
        comment={reportingComment}
      />
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={article?.title || ''}
        url={window.location.href}
        description={extractTextExcerpt(article?.content)}
      />
      <PostEditModal
        visible={!!editingPost}
        post={editingPost}
        onClose={() => setEditingPost(null)}
        onUpdated={() => {
          refetchArticle();
          setEditingPost(null);
        }}
      />
      {showLoginPrompt && (
        <LoginPrompt action={showLoginPrompt} onClose={() => setShowLoginPrompt(null)} />
      )}

      {/* Comment Drawer */}
      {commentDrawerOpen && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setCommentDrawerOpen(false)} />
          <div className="fixed top-0 right-0 h-full w-full md:w-[500px] bg-white dark:bg-gray-800 shadow-2xl z-50 overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Comments ({formatNumber(article.comments?.length || 0)})</h2>
              <button onClick={() => setCommentDrawerOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6">
              {!article.is_approved && (
                <div className="mb-6 text-yellow-700 bg-yellow-50 dark:text-yellow-200 dark:bg-yellow-900/30 px-4 py-3 rounded-lg text-sm">
                  {reviewStatus === 'requires_revision'
                    ? 'This post needs author revision. Reactions disabled until approved.'
                    : 'This post is awaiting approval. Commenting disabled.'}
                </div>
              )}

              {!user && !replyingTo && (
                <div className="mb-6">
                  <LoginPrompt action="comment" inline />
                </div>
              )}

              {user && !replyingTo && article.is_approved && (
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    rows={4}
                    placeholder="Share your thoughts..."
                  />
                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      onClick={() => setNewComment("")}
                      disabled={commentLoading}
                      className="px-4 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleComment}
                      disabled={commentLoading}
                      className="px-4 py-2 rounded-lg bg-primary hover:bg-[#229bb8] text-white text-sm font-semibold disabled:opacity-80"
                    >
                      {commentLoading ? "Posting..." : "Post"}
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {article.comments && article.comments.length > 0 ? (
                  article.comments.slice(0, commentsToShow).map(comment => renderComment(comment))
                ) : (
                  <div className="text-center py-10 text-gray-500 dark:text-gray-400">No comments yet. Be the first!</div>
                )}

                {article.comments && article.comments.length > commentsToShow && (
                  <button onClick={() => setCommentsToShow(prev => prev + 5)} className="w-full py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-gray-900 dark:text-white text-sm font-semibold">
                    Load More ({formatNumber(article.comments.length - commentsToShow)} remaining)
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}